import { Img } from '@/components/builder2/utils/Img';
import React, { forwardRef } from 'react';
import { useNode } from '@craftjs/core';
import { PROP_POSICION, normalizarPosicion } from '../utils/posicionLibre';

interface ImageProps {
  src?: string;
  alt?: string;
  width?: string;
  height?: string;
  padding?: number;
  margin?: number;
  borderRadius?: number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none';
  align?: 'left' | 'center' | 'right';
  /**
   * A dónde lleva la imagen al hacerle clic. Vacío o `#` = no lleva a ningún
   * lado, que es lo que hace TODA imagen ya guardada: sin este prop el DOM es
   * el mismo de siempre, sin un `<a>` de más.
   *
   * Un destino externo (`http…`) abre en una pestaña nueva; uno interno
   * (`/encuentra-tu-0-km`) navega en la misma. Mismo criterio que `Button`.
   */
  link?: string;
  /** El dealer la ocultó desde el editor sin borrarla: acá no se dibuja. */
  oculto?: boolean;
}

interface CraftComponent {
  craft: {
    displayName: string;
    props: Record<string, any>;
  };
}

/**
 * Cuando la imagen ya está posicionada en un lienzo, el envoltorio define el
 * frame y la imagen tiene que llenarlo.
 *
 * En el flujo la caja la marca la imagen; en un lienzo es al revés: el dealer
 * estira el frame con los cuadritos y la imagen lo acompaña. Sin esto quedaban
 * dos cajas distintas —el envoltorio con la medida elegida y la imagen con su
 * alto automático—, y entre ellas aparecía el hueco blanco.
 */
const ImageComponent = forwardRef<HTMLDivElement, ImageProps>(
  (
    {
      src = 'https://via.placeholder.com/300x200',
      alt = 'Image',
      width = '100%',
      height = 'auto',
      padding = 0,
      margin = 0,
      borderRadius = 4,
      objectFit = 'cover',
      align = 'left',
      oculto = false,
      link = '',
    }: ImageProps,
    ref
  ) => {
    const { connectors, selected, enLienzo } = useNode((state) => ({
      selected: state.events.selected,
      enLienzo: !!normalizarPosicion((state.data.props as Record<string, unknown>)?.[PROP_POSICION]),
    }));

    // Ocultarla es no dibujarla: en el editor sigue estando, tenue, para poder
    // volver a mostrarla.
    if (oculto) return null;

    /**
     * Dentro de un lienzo la imagen se AJUSTA al marco, nunca se recorta ni se
     * estira — y nunca se dibuja más grande que su archivo.
     *
     * El marco lo dibuja el dealer y casi nunca tiene la proporción exacta de la
     * foto; con `cover` esa diferencia se come los bordes, y como el ancho va en
     * PORCENTAJE y el alto en PÍXELES el recorte además crece con la pantalla.
     * Medido en los logos de B2 Autos: a 1440px Riddara perdía el 13.5%, y a
     * 1920px los cuatro perdían entre el 26% y el 35%.
     *
     * `scale-down` es `contain` con un tope: la imagen entra entera y centrada,
     * pero si el marco es más grande que el archivo NO se amplía. Eso es lo que
     * evita el logo borroso — KYC son 158x48 de origen y el marco de escritorio
     * le pedía 275px de ancho a 1920, o sea 1.74x. Ampliar no agrega información:
     * agrega desenfoque.
     *
     * Sólo cuando el dealer dejó `cover` o `contain`, que son las dos opciones de
     * "que entre en el marco". `fill` y `none` son elecciones explícitas de
     * deformar o no tocar, y se respetan tal cual.
     */
    const ajuste = enLienzo && (objectFit === 'cover' || objectFit === 'contain')
      ? 'scale-down'
      : objectFit;

    const marginLeft = align === 'left' ? '0' : 'auto';
    const marginRight = align === 'right' ? '0' : 'auto';

    // Vacío o `#` = sin destino. Es lo que tiene toda imagen ya guardada, y
    // por eso ahí no se emite ningún `<a>`: el DOM queda igual que siempre.
    const destino = typeof link === 'string' ? link.trim() : '';
    const enlazada = destino !== '' && destino !== '#';
    const externo = destino.startsWith('http');

    const marco = (
      <div
        ref={(node) => {
          if (node) {
            connectors.connect(node);
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }
        }}
        style={{
          padding: `${padding}px`,
          margin: `${margin}px 0`,
          borderRadius: `${borderRadius}px`,
          // En un lienzo el frame lo marca el envoltorio; el borde propio sumaba
          // a la caja y separaba la foto del marco.
          border: selected && !enLienzo ? '1px dashed #1e88e5' : 'none',
          ...(enLienzo ? { width: '100%', height: '100%', padding: 0, margin: 0 } : null),
        }}
      >
        <Img loading="eager"
          src={src}
          alt={alt}
          style={{
            width,
            height,
            display: 'block',
            marginLeft,
            marginRight,
            borderRadius: `${borderRadius}px`,
            objectFit: ajuste,
            // El frame lo marca el envoltorio y la foto lo llena: de ahí el
            // 100%/100%. El `object-fit` no: lo elige el dealer desde el
            // editor. Forzar `cover` acá lo dejaba mudo apenas la imagen
            // estaba posicionada, y encima obligaba a recortar — el frame
            // guarda `w` en PORCENTAJE y `h` en PÍXELES, así que su
            // proporción cambia con el ancho de la pantalla y el recorte
            // crece, o se da vuelta de lado, solo.
            //
            // MANTENER EN SYNC con goautos-admin.
            ...(enLienzo
              ? { width: '100%', height: '100%', marginLeft: 0, marginRight: 0 }
              : null),
          }}
        />
      </div>
    );

    if (!enlazada) return marco;

    // El `<a>` envuelve al frame y no al `<img>`: en un lienzo el frame ya
    // mide 100%/100% del envoltorio libre, y un ancla en el medio sin esas
    // medidas volvía a dejar la foto con alto automático — el mismo hueco
    // blanco que el frame vino a resolver.
    //
    // Externo en pestaña nueva con `noopener`, interno en la misma: mismo
    // criterio que `Button`. Al ser un `<a>` de verdad, el clic ya lo cuenta
    // `SiteEventTracker` sin llamar a nada.
    return (
      <a
        href={destino}
        {...(externo ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
        style={{
          display: 'block',
          ...(enLienzo ? { width: '100%', height: '100%' } : null),
        }}
      >
        {marco}
      </a>
    );
  }
);

ImageComponent.displayName = 'Image';

// Add craft property
(ImageComponent as unknown as CraftComponent).craft = {
  displayName: 'Image',
  props: {
    src: 'https://via.placeholder.com/300x200',
    alt: 'Image',
    width: '100%',
    height: 'auto',
    padding: 0,
    margin: 0,
    borderRadius: 4,
    objectFit: 'cover',
    align: 'left',
    link: '',
  },
};

export const Image = ImageComponent as typeof ImageComponent & CraftComponent;
